<script>
  import { onMount } from 'svelte';
  import Layout from '../components/Layout.svelte';
  import ErrorBanner from '../components/ErrorBanner.svelte';
  import EmptyState from '../components/EmptyState.svelte';
  import InfoHint from '../components/InfoHint.svelte';
  import PageLoader from '../components/PageLoader.svelte';
  import { userRolesApi } from '../stores/api.js';
  import { authStore } from '../stores/auth.js';
  import { setUserRoles } from '../stores/roles.js';
  import { t, tImmediate } from '../i18n/index.js';
  import { link } from 'svelte-spa-router';
  import { UserCog, Pencil, Save, X as XIcon, ShieldCheck } from '../lib/icons/index.js';

  /**
   * Global-role management — the first of the two permission layers.
   *
   * The second layer (which companies a person actually works in, and with
   * what role there) lives on each company's own page, because that is where
   * the pairing is stored: see CompanyMembers.svelte. Both layers are
   * explained on screen, since an installation where they disagree is the
   * single most confusing failure mode this system has — a user who sees the
   * buttons but is refused on save, or a company that is simply invisible
   * because nobody paired them with it.
   *
   * Rendered as the "Utenti" tab of Settings (embedded={true}), which is
   * gated on the global admin role. The /api/user-roles/* endpoints are
   * session-only and admin-only, so a context without one answers 401/403:
   * that is shown as a plain explanation, not as an error banner, because it
   * is a "not for you" and not a fault.
   */

  export let embedded = false;

  // Order roles from most to least powerful so the checkbox list reads as a
  // ladder rather than as an arbitrary set.
  const ROLE_ORDER = ['admin', 'operator', 'commercialista', 'readonly'];

  let users = [];
  let availableRoles = [];
  let loading = true;
  let error = null;
  // True when the API refused us (401/403): the tab is visible but the data
  // is not reachable from this kind of login.
  let unavailable = false;
  let editingId = null;
  let draftRoles = [];
  let saving = false;

  $: myUserId = $authStore.user?.id || null;

  onMount(load);

  async function load() {
    loading = true;
    error = null;
    unavailable = false;
    try {
      const [rolesRes, usersRes] = await Promise.all([
        userRolesApi.getAvailableRoles(),
        userRolesApi.getAllUsers(),
      ]);
      availableRoles = sortRoles(rolesRes?.roles || []);
      users = (usersRes?.users || [])
        .slice()
        .sort((a, b) => (a.email || a.id).localeCompare(b.email || b.id));
    } catch (err) {
      if (err.status === 401 || err.status === 403) {
        unavailable = true;
      } else {
        error = err;
      }
    } finally {
      loading = false;
    }
  }

  function sortRoles(roles) {
    return roles.slice().sort((a, b) => {
      const ia = ROLE_ORDER.indexOf(a);
      const ib = ROLE_ORDER.indexOf(b);
      return (ia === -1 ? ROLE_ORDER.length : ia) - (ib === -1 ? ROLE_ORDER.length : ib);
    });
  }

  function roleLabel(role) {
    return tImmediate(`users.roles.${role}`, undefined, role);
  }

  function openEdit(user) {
    editingId = user.id;
    draftRoles = sortRoles(user.roles || []);
    error = null;
  }

  function cancelEdit() {
    editingId = null;
    draftRoles = [];
  }

  function toggleDraft(role) {
    draftRoles = draftRoles.includes(role)
      ? draftRoles.filter(r => r !== role)
      : sortRoles([...draftRoles, role]);
  }

  async function saveRoles(user) {
    // Two ways to lock yourself (or everyone) out, both worth a question
    // first: dropping your own admin role, and leaving an account with no
    // role at all — which logs in but can reach nothing.
    const isSelf = user.id === myUserId;
    if (isSelf && (user.roles || []).includes('admin') && !draftRoles.includes('admin')) {
      if (!confirm(tImmediate('users.confirmDropOwnAdmin'))) return;
    }
    if (draftRoles.length === 0 && !confirm(tImmediate('users.confirmNoRoles'))) return;

    saving = true;
    error = null;
    try {
      const res = await userRolesApi.setRoles(user.id, draftRoles);
      const applied = sortRoles(res?.roles || draftRoles);
      users = users.map(u => (u.id === user.id ? { ...u, roles: applied } : u));
      // Editing yourself must take effect on the spot: the sidebar and every
      // permission gate read this store, and leaving it stale would show a
      // menu the server no longer honours.
      if (isSelf) setUserRoles(applied);
      cancelEdit();
    } catch (err) {
      error = err;
    } finally {
      saving = false;
    }
  }
</script>

{#snippet body()}
  <div class="page-header mb-4">
    {#if !embedded}
      <div class="flex items-center gap-2">
        <UserCog size={22} class="text-action" />
        <h1 class="section-title mb-0">{$t('users.title')}</h1>
      </div>
    {/if}
    <p class="text-muted text-sm mt-1 max-w-3xl">{$t('users.description')}</p>
  </div>

  <!-- The two layers, spelled out. This is the explainer the whole feature
       exists for: a company stays invisible until the pairing exists, and no
       error message anywhere says so. -->
  <div class="card p-5 mb-6">
    <h2 class="eyebrow eyebrow-lg mb-3 flex items-center gap-2" style="color: var(--color-primary);">
      <ShieldCheck size={16} />
      {$t('users.twoLayers.title')}
    </h2>
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <div class="p-3 border-w" style="border-color: var(--color-tint-medium);">
        <p class="eyebrow mb-1" style="color: var(--color-text-heading);">{$t('users.twoLayers.globalTitle')}</p>
        <p class="text-sm text-muted">{$t('users.twoLayers.globalBody')}</p>
      </div>
      <div class="p-3 border-w" style="border-color: var(--color-tint-medium);">
        <p class="eyebrow mb-1" style="color: var(--color-text-heading);">{$t('users.twoLayers.companyTitle')}</p>
        <p class="text-sm text-muted">{$t('users.twoLayers.companyBody')}</p>
        <a href="/companies" use:link class="eyebrow text-action hover:text-action-strong mt-2 inline-block">
          {$t('users.twoLayers.companyLink')}
        </a>
      </div>
    </div>
    <div class="alert alert-info mt-4">
      <span class="alert-mark" aria-hidden="true">i</span>
      <div>
        <p class="alert-title">{$t('users.twoLayers.conflictTitle')}</p>
        <p class="alert-message mt-1">{$t('users.twoLayers.conflictBody')}</p>
      </div>
    </div>
  </div>

  {#if unavailable}
    <div class="card p-8 text-center text-muted">{$t('users.unavailable')}</div>
  {:else}
    <ErrorBanner {error} />

    {#if loading}
      <PageLoader label={$t('users.loading')} />
    {:else if users.length === 0}
      <div class="card">
        <EmptyState icon={UserCog} title={$t('users.emptyTitle')} body={$t('users.empty')} />
      </div>
    {:else}
      <div class="card p-0 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="table-modern">
            <thead class="table-header">
              <tr>
                <th class="table-cell table-header-cell text-left">{$t('users.fields.account')}</th>
                <th class="table-cell table-header-cell text-left">{$t('users.fields.globalRoles')}</th>
                <th class="table-cell table-header-cell text-right"></th>
              </tr>
            </thead>
            <tbody>
              {#each users as user (user.id)}
                <tr class="table-row" style="cursor: default;">
                  <td class="table-cell align-top">
                    <div class="flex items-center gap-2">
                      <span class="font-bold text-heading break-all">{user.email || $t('users.noEmail')}</span>
                      {#if user.id === myUserId}
                        <span class="badge badge-cyan">{$t('users.youBadge')}</span>
                      {/if}
                    </div>
                    <p class="text-xs text-muted font-mono mt-1 break-all">{user.id}</p>
                  </td>
                  <td class="table-cell align-top">
                    {#if editingId === user.id}
                      <fieldset class="border-0 p-0 m-0">
                        <legend class="sr-only">{$t('users.editLegend', { account: user.email || user.id })}</legend>
                        <div class="space-y-2">
                          {#each availableRoles as role (role)}
                            <label class="flex items-start gap-2 text-sm">
                              <input type="checkbox" class="mt-1" checked={draftRoles.includes(role)} on:change={() => toggleDraft(role)} />
                              <span>
                                <span class="font-bold text-heading">{roleLabel(role)}</span>
                                <span class="block text-xs text-muted">{$t(`users.roleHints.${role}`, undefined, '')}</span>
                              </span>
                            </label>
                          {/each}
                        </div>
                      </fieldset>
                    {:else if (user.roles || []).length === 0}
                      <span class="text-xs text-muted">{$t('users.noRoles')}</span>
                    {:else}
                      <div class="flex flex-wrap gap-1">
                        {#each user.roles as role (role)}
                          <span class="badge badge-cyan" title={$t(`users.roleHints.${role}`, undefined, role)}>{roleLabel(role)}</span>
                        {/each}
                      </div>
                    {/if}
                  </td>
                  <td class="table-cell text-right align-top">
                    {#if editingId === user.id}
                      <div class="flex items-center gap-2 justify-end">
                        <button type="button" class="btn btn-secondary btn-sm" on:click={cancelEdit} disabled={saving}>
                          <XIcon size={14} class="mr-1" /> {$t('common.cancel')}
                        </button>
                        <button type="button" class="btn btn-accent btn-sm" on:click={() => saveRoles(user)} disabled={saving}>
                          <Save size={14} class="mr-1" /> {saving ? $t('users.saving') : $t('common.save')}
                        </button>
                      </div>
                    {:else}
                      <button type="button" class="p-1 hover:text-action-strong" title={$t('users.editRoles')}
                        aria-label={$t('users.editLegend', { account: user.email || user.id })}
                        on:click={() => openEdit(user)}>
                        <Pencil size={16} />
                      </button>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>

      <p class="text-xs text-muted mt-3 max-w-3xl">
        {$t('users.listedHint')}
        <InfoHint text={$t('users.listedHintLong')} />
      </p>
    {/if}
  {/if}
{/snippet}

{#if embedded}
  {@render body()}
{:else}
  <Layout>
    {@render body()}
  </Layout>
{/if}
